import React from 'react';
import { useField, useFormikContext } from 'formik';
import ReactFlagsSelect from 'react-flags-select';
import {
  Box,
  InputLabel,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

interface iFormikCountry {
  name: string;
  label?: string;
  maxHeight?: number;
}

interface iStyledSelect {
  maxHeight?: number;
}

const StyledSelect = styled(ReactFlagsSelect)<iStyledSelect>(
  ({ theme, maxHeight = 220 }) => ({
    '& > button': {
      borderColor: theme.palette.grey[300],
      boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.05)',
      borderWidth: 1,
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 500,

      '&:hover': {
        borderColor: theme.palette.grey[400],
      },

      '&:focus': {
        borderColor: theme.palette.grey[500],
        borderWidth: 1,
      },
    },

    '& > ul': {
      maxHeight,
      boxShadow: '0 7px 14px 0 #3c425714,0 3px 6px 0 #0000001f',
      border: 'none',
      borderRadius: 4,
      margin: 0,
      marginTop: 4,
      padding: 0,

      '& > div': {
        padding: 0,

        '& > input': {
          margin: 0,
          width: '100%',
          fontSize: 14,
          border: 'none',
          outline: 'none',
          borderBottom: '1px solid #eae9fa',
          borderRadius: 0,
        },
      },

      '& > li': {
        transition: 'all 0.3s ease',

        '&:hover': {
          backgroundColor: '#eae9fa',
        },
      },

      '& span': {
        fontSize: 14,
      },
    },
  })
);

const FormikCountry: React.FC<iFormikCountry> = ({
  name,
  label,
  maxHeight,
}) => {
  const [field, meta] = useField(name);
  const { setFieldTouched } = useFormikContext();

  const theme = useTheme();
  const medium = useMediaQuery(theme.breakpoints.up('md'));
  const space = medium ? 4 : 2;

  const { touched, error, value } = meta;
  const { onChange } = field;

  return (
    <Box mt={label ? space : 0} onBlur={() => setFieldTouched(name)}>
      <InputLabel
        htmlFor={name}
        sx={{
          fontSize: 14,
          fontWeight: 500,
          color: 'text.primary',
          mb: 1,
        }}
      >
        {label}
      </InputLabel>
      <StyledSelect
        maxHeight={maxHeight}
        searchable={true}
        showSelectedLabel={true}
        showOptionLabel={true}
        onSelect={(code) => onChange({ target: { name, value: code } })}
        selected={value}
        id={name}
        searchPlaceholder="Search..."
        selectedSize={14}
      />
      {touched && error && (
        <Typography
          variant="body2"
          sx={{ color: 'error.main', mt: 1, fontSize: 13 }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default FormikCountry;
